import { v4 as uuidv4 } from 'uuid';

import {
  BRIDGE_VERSION,
  BridgeMessage,
  NativeMessageHandler,
  NativeMessageType,
  NativePlatform,
  NativeAuthData,
  NativeToWebMessage,
  OutboundMessageInput,
  ResponseMessage,
  WebToNativeMessage,
} from './types';

const DEFAULT_TIMEOUT_MS = 5000;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

const isBrowser = typeof window !== 'undefined';

/**
 * True when the page is running inside the Expo WebView shell (or a dev
 * simulation that has injected `window.__VMALL_NATIVE__`). Use this for UI
 * gating — hide login links, redirect auth pages, etc.
 *
 * Note: real device also has `window.ReactNativeWebView`; the dev console
 * simulation script does not. `request()` / `notify()` check that separately
 * and degrade gracefully when it's missing.
 */
export function isInNativeApp(): boolean {
  if (!isBrowser) return false;
  return !!window.__VMALL_NATIVE__;
}

function canPostToNative(): boolean {
  return isBrowser && !!window.ReactNativeWebView;
}

export function getNativePlatform(): NativePlatform | null {
  if (!isBrowser) return null;
  return window.__VMALL_NATIVE__?.platform ?? null;
}

export function getNativeAuth(): NativeAuthData | null {
  if (!isBrowser) return null;
  return window.__VMALL_NATIVE__?.auth ?? null;
}

class NativeBridge {
  private pending = new Map<string, PendingRequest>();
  private handlers = new Map<NativeMessageType, Set<(...args: unknown[]) => void>>();
  private started = false;

  constructor() {
    if (isBrowser) {
      this.start();
    }
  }

  private start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('nativeMessage', this.handleNativeMessage as EventListener);
  }

  private handleNativeMessage = (event: Event): void => {
    const messageEvent = event as MessageEvent<string>;
    let parsed: BridgeMessage;
    try {
      parsed =
        typeof messageEvent.data === 'string'
          ? (JSON.parse(messageEvent.data) as BridgeMessage)
          : (messageEvent.data as BridgeMessage);
    } catch (err) {
      console.warn('[NativeBridge] Failed to parse native message:', err);
      return;
    }

    if (!parsed || typeof parsed.type !== 'string') {
      console.warn('[NativeBridge] Invalid native message:', parsed);
      return;
    }

    if (parsed.id && this.pending.has(parsed.id)) {
      const pending = this.pending.get(parsed.id)!;
      clearTimeout(pending.timer);
      this.pending.delete(parsed.id);
      pending.resolve((parsed as ResponseMessage).payload);
      return;
    }

    const handlers = this.handlers.get(parsed.type as NativeMessageType);
    if (handlers && handlers.size > 0) {
      const payload = (parsed as { payload?: unknown }).payload;
      handlers.forEach((handler) => {
        try {
          handler(payload, parsed);
        } catch (err) {
          console.warn(`[NativeBridge] Handler for ${parsed.type} threw:`, err);
        }
      });
    }
  };

  request<TResponsePayload = unknown>(
    msg: OutboundMessageInput<WebToNativeMessage>,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<TResponsePayload> {
    if (!canPostToNative()) {
      return Promise.reject(new Error('Native bridge channel unavailable'));
    }
    const id = uuidv4();
    const envelope: BridgeMessage = {
      ...msg,
      id,
      timestamp: Date.now(),
      version: BRIDGE_VERSION,
    } as BridgeMessage;

    return new Promise<TResponsePayload>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Bridge request timed out: ${msg.type}`));
      }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });

      try {
        window.ReactNativeWebView!.postMessage(JSON.stringify(envelope));
      } catch (err) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(err);
      }
    });
  }

  notify(msg: OutboundMessageInput<WebToNativeMessage>): void {
    if (!canPostToNative()) return;
    const envelope: BridgeMessage = {
      ...msg,
      id: '',
      timestamp: Date.now(),
      version: BRIDGE_VERSION,
    } as BridgeMessage;
    try {
      window.ReactNativeWebView!.postMessage(JSON.stringify(envelope));
    } catch (err) {
      console.warn('[NativeBridge] notify failed:', err);
    }
  }

  on<T extends NativeMessageType>(
    type: T,
    handler: NativeMessageHandler<T>,
  ): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const wrapped = handler as unknown as (...args: unknown[]) => void;
    set.add(wrapped);
    return () => {
      const current = this.handlers.get(type);
      if (!current) return;
      current.delete(wrapped);
      if (current.size === 0) this.handlers.delete(type);
    };
  }

  /** Test-only: simulate a Native → Web message in dev console. */
  _devDispatch(message: NativeToWebMessage): void {
    window.dispatchEvent(
      new MessageEvent('nativeMessage', { data: JSON.stringify(message) }),
    );
  }
}

export const nativeBridge = new NativeBridge();
